// uiarc: progress (https://uiarc.dev/r/progress.json) — Base UI Progress (erişilebilirlik: rol, aria-valuenow) +
// Tailwind. Tek bir yay hem dolguyu hem sayılan etiketi sürer, sayı hep çubukla eşleşir. Dolgu soldan kayar
// (ölçeklenmez), yuvarlak ucu her değerde şeklini korur. %100'de dolgu başarı rengine döner ve değerin yanına tik oturur.
// `prefers-reduced-motion` altında hareket yok. Marka vurgusu `--brand`.
import { Progress as ProgressPrimitive } from '@base-ui/react/progress'
import { Check } from 'lucide-react'
import {
  AnimatePresence,
  animate,
  motion,
  useIsPresent,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from 'motion/react'
import type { HTMLMotionProps, TargetAndTransition, Transition } from 'motion/react'
import { useEffect } from 'react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

const exitFast: Transition = {
  duration: motionTokens.duration.fast,
  ease: motionTokens.ease.standard,
}
const textIn: TargetAndTransition = {
  opacity: 0,
  y: '0.3em',
  filter: `blur(${motionTokens.blur.soft}px)`,
}
const textOut: TargetAndTransition = {
  opacity: 0,
  y: '-0.3em',
  filter: `blur(${motionTokens.blur.subtle}px)`,
  transition: exitFast,
}
const iconIn: TargetAndTransition = {
  opacity: 0,
  scale: 0.6,
  filter: `blur(${motionTokens.blur.subtle}px)`,
}
const shown: TargetAndTransition = { opacity: 1, y: '0em', scale: 1, filter: 'blur(0px)' }
const fadeOut: TargetAndTransition = {
  opacity: 0,
  transition: { duration: motionTokens.duration.instant },
}

/** Çıkmakta olan kopyalar solarken yardımcı teknolojiden gizlenir. */
function Swap(props: HTMLMotionProps<'span'>) {
  const present = useIsPresent()
  return <motion.span {...props} aria-hidden={present ? props['aria-hidden'] : true} />
}

type ProgressProps = Omit<ProgressPrimitive.Root.Props, 'value'> & {
  value?: number
  /** Görünür etiket; değişince eski yazı yukarı çıkar, yenisi yükselir. */
  label?: string
  /** Sağda sayılan yüzdeyi (ve %100'de tiki) gösterir. */
  showValue?: boolean
}

function Progress({
  className,
  children,
  value = 0,
  max = 100,
  label,
  showValue = false,
  ...props
}: ProgressProps) {
  const reduce = useReducedMotion()
  const safeMax = max > 0 ? max : 100
  const safeValue = Math.min(Math.max(value, 0), safeMax)
  const percentage = Math.round((safeValue / safeMax) * 100)
  const complete = percentage >= 100
  // Tek yay hem dolguyu hem sayılan etiketi sürer; sayı hep çubukla eşleşir.
  const progress = useMotionValue(percentage)
  // Dolgu soldan kayar, ölçeklenmez: yuvarlak ucu her değerde şeklini korur.
  const x = useTransform(progress, (latest) => `${Math.min(Math.max(latest, 0), 100) - 100}%`)
  const counted = useTransform(
    progress,
    (latest) => `${Math.round(Math.min(Math.max(latest, 0), 100))}%`,
  )
  useEffect(() => {
    if (reduce) {
      progress.jump(percentage)
      return
    }
    const controls = animate(progress, percentage, motionTokens.spring.smooth)
    return () => controls.stop()
  }, [percentage, progress, reduce])
  const enter: Transition = reduce
    ? { duration: motionTokens.duration.instant }
    : { duration: motionTokens.duration.standard, ease: motionTokens.ease.enter }

  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      data-complete={complete ? '' : undefined}
      value={safeValue}
      max={safeMax}
      getAriaValueText={() => `${percentage}%`}
      className={cn('group/progress text-text-secondary w-full text-sm', className)}
      {...props}
    >
      {label || showValue || children ? (
        <div className="leading-body mb-2 flex justify-between gap-3">
          {label ? (
            <span data-slot="progress-label" className="relative min-w-0">
              <AnimatePresence mode="popLayout" initial={false}>
                <Swap
                  key={label}
                  className="block"
                  initial={reduce ? { opacity: 0 } : textIn}
                  animate={shown}
                  exit={reduce ? fadeOut : textOut}
                  transition={enter}
                >
                  {label}
                </Swap>
              </AnimatePresence>
            </span>
          ) : (
            <span />
          )}
          {children}
          {showValue ? (
            <span
              data-slot="progress-value"
              className="text-text-muted inline-flex flex-none items-center gap-1 tabular-nums"
            >
              {/* Tamamlanma dolgu varırken oturur: son sayının yanına tik yerleşir. */}
              <AnimatePresence initial={false}>
                {complete && (
                  <motion.span
                    key="done"
                    className="text-success grid place-items-center"
                    initial={reduce ? { opacity: 0 } : iconIn}
                    animate={shown}
                    exit={reduce ? fadeOut : { ...iconIn, transition: exitFast }}
                    transition={reduce ? enter : { ...motionTokens.spring.snappy, delay: 0.24 }}
                  >
                    <Check size={14} strokeWidth={1.75} aria-hidden="true" />
                  </motion.span>
                )}
              </AnimatePresence>
              {/* Gizli "100%" satırı en geniş sayıyı ayırır: basamak artınca tik kaymaz. */}
              <motion.span className="inline-grid justify-items-end before:invisible before:h-0 before:overflow-hidden before:content-['100%']">
                {counted}
              </motion.span>
            </span>
          ) : null}
        </div>
      ) : null}
      <ProgressTrack>
        <motion.span
          data-slot="progress-indicator"
          className="bg-brand ease-standard group-data-[complete]/progress:bg-success block h-full w-full rounded-[inherit] transition-[background-color] duration-(--duration-standard) group-data-[complete]/progress:delay-200 motion-reduce:transition-none"
          style={{ x }}
        />
      </ProgressTrack>
    </ProgressPrimitive.Root>
  )
}

function ProgressTrack({ className, ...props }: ProgressPrimitive.Track.Props) {
  return (
    <ProgressPrimitive.Track
      className={cn(
        'border-border bg-surface-muted relative block h-[7px] w-full overflow-hidden rounded-[99px] border',
        className,
      )}
      data-slot="progress-track"
      {...props}
    />
  )
}

function ProgressIndicator({ className, ...props }: ProgressPrimitive.Indicator.Props) {
  return (
    <ProgressPrimitive.Indicator
      data-slot="progress-indicator"
      className={cn('bg-brand block h-full rounded-[inherit] transition-all', className)}
      {...props}
    />
  )
}

function ProgressLabel({ className, ...props }: ProgressPrimitive.Label.Props) {
  return (
    <ProgressPrimitive.Label
      className={cn('text-text-secondary text-sm', className)}
      data-slot="progress-label"
      {...props}
    />
  )
}

function ProgressValue({ className, ...props }: ProgressPrimitive.Value.Props) {
  return (
    <ProgressPrimitive.Value
      className={cn('text-text-muted ml-auto text-sm tabular-nums', className)}
      data-slot="progress-value"
      {...props}
    />
  )
}

export { Progress, ProgressTrack, ProgressIndicator, ProgressLabel, ProgressValue }
